import React from "react";
import { Form } from "react-final-form";
import { Grid, Box } from "@mui/material";

/**
 * RffForm — standard form wrapper around react-final-form's <Form>. By default
 * children render inside a legacy MUI Grid container (Rff* fields lay out as
 * Grid items). Pass `disableGrid` to render children directly instead and lay
 * the form out with <FormGrid> / <FormSection> — the field-agnostic CSS-grid
 * approach.
 *
 * NOTE: `keepDirtyOnReinitialize` defaults to true, which breaks `form.reset()` —
 * final-form implements reset as initialize(), and initialize() re-applies the
 * dirty values on purpose. For a Cancel button use
 * utils/finalForm/rffResetToInitialValues instead.
 *
 * Props:
 *   initialValues — final-form initialValues
 *   onSubmit — final-form onSubmit
 *   spacing — Grid spacing (default 2); ignored when disableGrid is set
 *   disableGrid — skip the MUI Grid container; children own their layout
 *   children — either a render function (handleSubmit, formApi) => JSX, or JSX
 */
const RffForm = ({
  initialValues,
  onSubmit,
  spacing = 2,
  disableGrid = false,
  validate,
  mutators,
  decorators,
  keepDirtyOnReinitialize = true,
  children,
}) => (
  <Form
    onSubmit={onSubmit}
    initialValues={initialValues}
    validate={validate}
    mutators={mutators}
    decorators={decorators}
    keepDirtyOnReinitialize={keepDirtyOnReinitialize}
    render={({ handleSubmit, ...formApi }) => {
      const content =
        typeof children === "function"
          ? children({ handleSubmit, ...formApi })
          : children;
      return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {disableGrid ? (
            content
          ) : (
            <Grid container spacing={spacing}>
              {content}
            </Grid>
          )}
        </Box>
      );
    }}
  />
);

export default RffForm;
